/**
 * Whether dragging one box onto another is a legal re-parent, and if not, why.
 *
 * Pure and tested directly, for the same reason the layout is: the interesting
 * case is the one that must be REFUSED, and a refusal is invisible in a render
 * test that only ever performs a legal drag.
 *
 * The refusal names the element. "Invalid parent" tells somebody nothing they
 * can act on; "that would make SUB_STR_2 its own ancestor, via SUB_STR_2 →
 * DET_STR_1" tells them exactly which trace is in the way, and they can go and
 * look at it. The back end would refuse a cycle too, but only after a round
 * trip, and its message is about rows rather than about the diagram in front of
 * them.
 */

/** The least a node has to be for this check — the diagram's own nodes fit. */
export interface Traceable {
  key: string
  /** What to call it in a sentence: the model's ID where there is one. */
  label: string
  parentKeys: string[]
}

export interface Reparent {
  allowed: boolean
  /** Empty when allowed. Otherwise a whole sentence naming the elements. */
  reason: string
}

/**
 * Walk up from `key`, stopping at the root or at a cycle already in the data.
 *
 * The `seen` guard is not defensive politeness: the model can hold a cycle
 * (that is `parent-cycle`, a finding, not a parse error) and this function is
 * called while somebody is dragging, so looping forever would hang the tab.
 *
 * @param[in] byKey - Map : every node, keyed
 * @param[in] key - string : where to start, exclusive
 * @return string[] - every key above this one, in no particular order
 */
function ancestry(byKey: Map<string, Traceable>, key: string): string[] {
  // A SEARCH, not a walk. Since issue #32 a node may name several sources, so
  // everything above it is a set reached by exploring every branch — following
  // one would miss a cycle that closes through another.
  const found: string[] = []
  const seen = new Set<string>([key])
  const stack = [...(byKey.get(key)?.parentKeys ?? [])]
  while (stack.length) {
    const current = stack.pop() as string
    if (seen.has(current)) continue
    seen.add(current)
    found.push(current)
    stack.push(...(byKey.get(current)?.parentKeys ?? []))
  }
  return found
}

/**
 * May `childKey` be re-parented onto `newParentKey`?
 *
 * The four refusals, in the order they are checked:
 *
 *  1. either end is not on this diagram — a stale drag after a refresh;
 *  2. dropping a node on itself;
 *  3. the trace already exists, so the write would be a no-op;
 *  4. the proposed parent is a DESCENDANT of the child, which is the cycle.
 *
 * (4) is the one this file exists for. It is checked by walking up from the
 * proposed parent: if the child is anywhere above it, then making it the child's
 * parent puts the child above itself.
 *
 * @param[in] nodes - Traceable[] : every node currently drawn
 * @param[in] childKey - string : the node being dragged
 * @param[in] newParentKey - string : the node it was dropped on
 * @return Reparent
 */
export function checkReparent(
  nodes: Traceable[],
  childKey: string,
  newParentKey: string,
): Reparent {
  const byKey = new Map(nodes.map((node) => [node.key, node]))
  const child = byKey.get(childKey)
  const parent = byKey.get(newParentKey)

  if (!child || !parent) {
    return {
      allowed: false,
      reason: 'One of those is no longer on the diagram. Reload the view and try again.',
    }
  }

  if (childKey === newParentKey) {
    return { allowed: false, reason: `${child.label} cannot derive from itself.` }
  }

  if (child.parentKeys.includes(newParentKey)) {
    return { allowed: false, reason: `${child.label} already derives from ${parent.label}.` }
  }

  const above = ancestry(byKey, newParentKey)
  const reach = above.indexOf(childKey)
  if (reach !== -1) {
    // The trace that is in the way, written the way it is drawn: from the
    // proposed parent up through to the child that already sits above it.
    const via = [parent.label, ...above.slice(0, reach + 1).map((key) => byKey.get(key)?.label ?? key)]
    return {
      allowed: false,
      reason:
        `That would make ${child.label} its own ancestor: ` +
        `${parent.label} already derives from it, via ${via.join(' → ')}.`,
    }
  }

  return { allowed: true, reason: '' }
}
